// Gerçek zaman istemcisi: /v1/ws — kişisel kanal ("me") + istenen kanallar (trip:<id>, child:<id>).
// Bağlantı koparsa üstel geri çekilmeyle yeniden bağlanır; süresi dolan belirteçte önce yeniler.
import type { TokenStore } from './client';

export type RealtimeEvent = { type: string; [k: string]: unknown };
type Handler = (e: RealtimeEvent) => void;

export interface Realtime {
  start(): void;
  stop(): void;
  subscribe(channel: string): void;
  unsubscribe(channel: string): void;
  on(h: Handler): () => void;
  reset(): void;
}

export function createRealtime(opts: {
  apiUrl: string;
  tokens: TokenStore;
  refresh: () => Promise<unknown>;
  /** panelde seçili firma (`ops`, `company` kanalları firma bağlamıyla yetkilendirilir) */
  getCompanyId?: () => string | null;
}): Realtime {
  let ws: WebSocket | null = null;
  let channels = new Set<string>(['me']);
  // aynı kanalı birden çok ekran dinleyebilir (ana sayfa + canlı takip): son dinleyici bırakınca abonelik düşer
  let refs = new Map<string, number>();
  const handlers = new Set<Handler>();
  let retry = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = true;

  const schedule = () => {
    if (stopped || timer) return;
    const delay = Math.min(30_000, 1000 * 2 ** retry++);
    timer = setTimeout(() => {
      timer = null;
      void connect();
    }, delay);
  };

  const connect = async () => {
    if (stopped) return;
    const t = await opts.tokens.get();
    if (!t) return;
    try {
      const company = opts.getCompanyId?.();
      ws = new WebSocket(
        `${opts.apiUrl.replace(/^http/, 'ws')}/v1/ws?token=${encodeURIComponent(t.accessToken)}${company ? `&company=${encodeURIComponent(company)}` : ''}`,
      );
    } catch {
      schedule();
      return;
    }
    ws.onopen = () => {
      retry = 0;
    };
    ws.onmessage = (m) => {
      let e: RealtimeEvent;
      try {
        e = JSON.parse(String(m.data)) as RealtimeEvent;
      } catch {
        return;
      }
      if (e.type === 'ready') for (const c of channels) ws?.send(JSON.stringify({ type: 'subscribe', channel: c }));
      handlers.forEach((h) => h(e));
    };
    ws.onclose = (ev) => {
      ws = null;
      // süresi dolan erişim belirteci: bir istek yenilemeyi tetikler, sonra yeniden bağlanılır
      if (ev.code === 4401)
        void opts
          .refresh()
          .catch(() => undefined)
          .finally(schedule);
      else schedule();
    };
    ws.onerror = () => ws?.close();
  };

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      void connect();
    },
    stop() {
      stopped = true;
      if (timer) clearTimeout(timer);
      timer = null;
      ws?.close();
      ws = null;
    },
    subscribe(channel) {
      const n = (refs.get(channel) ?? 0) + 1;
      refs.set(channel, n);
      if (n > 1) return;
      channels.add(channel);
      if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'subscribe', channel }));
    },
    unsubscribe(channel) {
      const n = (refs.get(channel) ?? 0) - 1;
      if (n > 0) {
        refs.set(channel, n);
        return;
      }
      refs.delete(channel);
      channels.delete(channel);
      if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'unsubscribe', channel }));
    },
    on(h) {
      handlers.add(h);
      return () => handlers.delete(h);
    },
    reset() {
      channels = new Set(['me']);
      refs = new Map();
    },
  };
}
